/**
 * senderTone — deterministic visual tinting for sender avatars in the
 * inbox.
 *
 * Why
 * ---
 * The inbox lives or dies by **scan speed**: the user wants to spot
 * "another mail from Mario" three rows away without parsing the
 * sender line. A stable per-sender hue makes the avatar a chromatic
 * anchor — same hash ⇒ same colour ⇒ same person.
 *
 * Approach
 * --------
 * djb2-style hash over the **address** (the part before/at ``@``) —
 * NOT the display name, which can flip between ``Mario Rossi`` and
 * ``Rossi, Mario`` depending on the provider. We pick from a fixed
 * 12-tone palette derived from the existing Tailwind colour ramp so
 * every chip stays inside the Ando design language (warm-only, no
 * random HSL — keeps dark/light variants pre-validated).
 *
 * Token contract
 * --------------
 * The output is a static Tailwind class pair (``bg-*`` + ``text-*``)
 * so Tailwind's JIT picks it up at build time. NEVER return an
 * interpolated class — the compiler would purge it.
 */

/**
 * WARM-ONLY hue×ramp pairs — soft background + dark foreground so the
 * initial stays readable on the bg without manual contrast tuning. Every
 * family sits on the warm side of the wheel (amber / orange / stone /
 * rose / emerald / yellow) so avatars never break the Ando "pencil on
 * cream" language with a cold-hued chip.
 *
 * The PEC channel still uses ``bg-ando-orange-soft / text-ando-orange``
 * unconditionally (orange = certified mail); the ramp below leans on the
 * softer amber/orange tints so it stays distinct from that brand accent.
 */
const SENDER_TONES: ReadonlyArray<{ readonly bg: string; readonly fg: string }> = [
  { bg: "bg-amber-50", fg: "text-amber-800" },
  { bg: "bg-orange-50", fg: "text-orange-800" },
  { bg: "bg-stone-100", fg: "text-stone-700" },
  { bg: "bg-rose-50", fg: "text-rose-800" },
  { bg: "bg-emerald-50", fg: "text-emerald-800" },
  { bg: "bg-yellow-50", fg: "text-yellow-800" },
];

/** djb2 string hash → unsigned 32-bit int. Pure, deterministic, fast. */
function djb2(input: string): number {
  let h = 5381;
  for (let i = 0; i < input.length; i += 1) {
    // h * 33 + char — sign-preserving via |0 keeps the result a safe int.
    h = ((h << 5) + h + input.charCodeAt(i)) | 0;
  }
  return h >>> 0;
}

/**
 * Resolve a stable address-ish identifier for a sender string. Prefers
 * the angle-bracketed email when present (``Mario Rossi <mario@x.it>``)
 * so a display-name change doesn't re-roll the colour. Falls back to
 * the trimmed whole string.
 */
export function senderHashKey(sender: string): string {
  const trimmed = sender.trim().toLowerCase();
  if (trimmed.length === 0) return "?";
  const match = trimmed.match(/<([^>]+)>/);
  return (match?.[1] ?? trimmed).trim();
}

/**
 * Pick a stable ``{ bg, fg }`` token pair for a sender string.
 *
 * Same address → same pair forever. Empty / undefined senders fall
 * back to a neutral warm stone tone (``bg-stone-100 text-stone-600``)
 * so the avatar still renders without exposing the palette to fallback
 * rows.
 */
export function senderTone(sender: string | undefined | null): {
  readonly bg: string;
  readonly fg: string;
} {
  if (!sender || sender.trim().length === 0) {
    return { bg: "bg-stone-100", fg: "text-stone-600" };
  }
  const key = senderHashKey(sender);
  const idx = djb2(key) % SENDER_TONES.length;
  // Non-null assert is safe: idx is bounded by the array length.
  return SENDER_TONES[idx] as { readonly bg: string; readonly fg: string };
}

/** Two-letter initials from a sender display string. Empty → "??". */
export function senderInitials(sender: string | undefined | null): string {
  const trimmed = (sender ?? "").trim();
  if (!trimmed) return "??";
  // Strip an angle-bracketed email so the initials come from the
  // friendly name when present.
  const friendly = trimmed.replace(/<[^>]+>/, "").trim() || trimmed;
  const parts = friendly.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    const a = parts[0]?.[0] ?? "";
    const b = parts[1]?.[0] ?? "";
    return (a + b).toUpperCase();
  }
  return friendly.slice(0, 2).toUpperCase();
}
